import type { LayerState } from '@vitals/shared';
import type { VitalDef } from './vitals.js';

interface TidePrediction {
  time: string;
  feet: number;
  type: 'high' | 'low';
  elapsed: boolean;
}

interface TidesSummary {
  feet: number;
  trend: 'rising' | 'falling';
  observedAt?: string;
  predictions?: TidePrediction[];
}

function timeLabel(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (hour > 23 || minute > 59) return null;
  return `${hour % 12 || 12}:${match[5]} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * Vital selector for the NOAA tides layer.
 *
 * Display: "3.1 ft ↑" (↑ rising / ↓ falling) / label "tide".
 * Down/no-summary: value "—", label "tide", down true.
 */
export const tidesVital: VitalDef = {
  id: 'tide',
  layerId: 'tides',

  select(s: LayerState | undefined) {
    const down = !s || s.status === 'down';
    const stale = s?.status === 'stale';

    if (down || !s?.summary) {
      return { value: '—', label: 'tides', down: true, stale };
    }

    const summary = s.summary as unknown as TidesSummary;
    const { feet, trend } = summary;
    const arrow = trend === 'rising' ? '↑' : '↓';

    const observedTime = timeLabel(summary.observedAt);
    const nowValue = `${feet.toFixed(1)} ft · ${trend === 'rising' ? 'Rising' : 'Falling'}` +
      (observedTime ? ` · ${observedTime}` : '');
    const predictionDetails = (Array.isArray(summary.predictions) ? summary.predictions : [])
      .flatMap((prediction) => {
        const time = timeLabel(prediction?.time);
        if (!time || !Number.isFinite(prediction?.feet) ||
          (prediction.type !== 'high' && prediction.type !== 'low')) return [];
        return [{
          kind: 'metric' as const,
          label: prediction.type === 'high' ? 'High' : 'Low',
          value: `${time} · ${prediction.feet.toFixed(1)} ft`,
          elapsed: prediction.elapsed === true,
        }];
      });

    return {
      value: `${feet.toFixed(1)} ft ${arrow}`,
      label: 'tide',
      down: false,
      stale,
      detailTitle: 'Today at The Battery',
      details: [
        { kind: 'metric', label: 'Now', value: nowValue },
        ...predictionDetails,
        { kind: 'note', text: 'Levels relative to MLLW' },
      ],
    };
  },
};
